import { Link } from "react-router-dom";
import { Section, FinalCTA } from "@/components/v2/blocks";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import { Layers, ClipboardCheck, Target, Eye, Repeat, ReceiptText, Mail, ArrowRight, FileText, LifeBuoy, SlidersHorizontal, Home } from "lucide-react";

const PLUS_LOIN = [
  { to: "/particuliers/changer-de-fournisseur", label: "Changer de fournisseur", sub: "Particuliers", icon: Repeat },
  { to: "/particuliers/comprendre-sa-facture", label: "Comprendre sa facture", sub: "Particuliers", icon: ReceiptText },
  { to: "/contact", label: "Nous contacter", sub: "Étude gratuite", icon: Mail },
];

const POURQUOI = [
  { icon: Layers, title: "Mieux comprendre les offres", text: "Un panorama clair des fournisseurs et de leurs offres." },
  { icon: ClipboardCheck, title: "Faire le point sur sa situation", text: "Une analyse de votre consommation et de votre contrat actuel." },
  { icon: Target, title: "Identifier les bonnes solutions", text: "Les options réellement adaptées à votre foyer." },
  { icon: Eye, title: "Un regard extérieur", text: "Un avis neutre et pédagogique sur votre contrat." },
];

const AU_DELA = [
  { icon: FileText, title: "Les caractéristiques du contrat", text: "Durée, conditions, options et clauses à connaître." },
  { icon: LifeBuoy, title: "Les services associés", text: "Le support, les outils et l'accompagnement proposés." },
  { icon: SlidersHorizontal, title: "Les modalités de gestion", text: "Facturation, prélèvement, espace client, flexibilité." },
  { icon: Home, title: "Les besoins du foyer", text: "Consommation, équipements et habitudes propres à vous." },
];

function RevealCard({ icon: Icon, title, text }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-500 ease-out hover:-translate-y-3 hover:scale-[1.05] hover:border-primary hover:bg-primary hover:shadow-2xl hover:shadow-primary/40">
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: "radial-gradient(130% 90% at 50% 0%, rgba(196,240,66,0.30), transparent 62%)" }}
      />
      <div className="relative">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm transition-all duration-500 group-hover:scale-110 group-hover:bg-accent group-hover:text-accent-foreground group-hover:shadow-lg group-hover:shadow-accent/40">
          <Icon className="h-6 w-6" strokeWidth={2.1} aria-hidden />
        </div>
        <h3 className="font-display text-base font-bold text-primary mt-5 leading-snug transition-colors duration-500 group-hover:text-white">{title}</h3>
        <div className="grid grid-rows-[0fr] opacity-0 transition-all duration-500 ease-out group-hover:mt-2 group-hover:grid-rows-[1fr] group-hover:opacity-100">
          <p className="overflow-hidden text-sm leading-relaxed text-white/85">{text}</p>
        </div>
      </div>
    </div>
  );
}

export default function ComparerOffres() {
  return (
    <div>
      <PageHeroPhoto
        cmsKey="comparer"
        eyebrow="Particuliers · Comparer"
        title="Comparez les offres d'énergie en toute simplicité"
        subtitle="Face à la diversité des fournisseurs et des offres disponibles, il n'est pas toujours facile d'identifier la solution adaptée à son foyer. Energy Market vous accompagne dans l'analyse de votre situation afin de mieux comprendre les possibilités offertes par le marché."
        image="/img/comparer-offres.png"
        overlay="cinematic"
        flipLogo
        wideCard
        heroAside={
          <div className="flex justify-end -mr-2 lg:-mr-8">
            <div className="relative">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 360" role="img" aria-label="Point d'interrogation" className="relative h-56 w-auto md:h-72">
              {/* trait toujours visible + fond du « ? » */}
              <path
                d="M42 82 C66 28, 122 10, 171 27 C219 44, 238 91, 225 132 C216 160, 196 178, 170 195 C143 213, 126 228, 126 260 L82 260 C82 211, 104 184, 136 162 C159 146, 177 133, 182 114 C189 88, 172 65, 145 61 C117 57, 91 72, 79 101 Z M104 301 A25 25 0 1 0 104 351 A25 25 0 1 0 104 301 Z"
                fill="rgba(255,255,255,0.22)"
                stroke="#5C7A2E"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* reflet lumineux : 1 passage sur la crosse du ? ... */}
              <path
                d="M42 82 C66 28, 122 10, 171 27 C219 44, 238 91, 225 132 C216 160, 196 178, 170 195 C143 213, 126 228, 126 260 L82 260 C82 211, 104 184, 136 162 C159 146, 177 133, 182 114 C189 88, 172 65, 145 61 C117 57, 91 72, 79 101 Z"
                fill="none"
                stroke="#C7FF2F"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="em-qmark-comet"
              />
              {/* ... pour 2 passages dans le point */}
              <path
                d="M104 301 A25 25 0 1 0 104 351 A25 25 0 1 0 104 301 Z"
                fill="none"
                stroke="#C7FF2F"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="em-qmark-comet--dot"
              />
              </svg>
            </div>
          </div>
        }
        highlightsLabel="Pourquoi comparer ?"
        highlights={[
          "Mieux comprendre les offres disponibles",
          "Faire le point sur sa situation",
          "Identifier les solutions adaptées à vos besoins",
          "Un regard extérieur sur votre contrat",
        ]}
      />

      <Section cmsKey="comparer.pourquoi" eyebrow="Pourquoi comparer ?" title="Faire le point sur sa situation" intro="Comparer, ce n'est pas seulement chercher le prix le plus bas : c'est mieux comprendre vos options pour décider sereinement.">
        <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {POURQUOI.map((c) => <RevealCard key={c.title} {...c} />)}
        </div>
      </Section>

      <Section cmsKey="comparer.audela" eyebrow="Au-delà du prix" title="Une comparaison ne se limite pas au tarif" intro="Une analyse pertinente prend également en compte plusieurs critères essentiels." muted>
        <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {AU_DELA.map((c) => <RevealCard key={c.title} {...c} />)}
        </div>
      </Section>

      <Section title="Pour aller plus loin">
        <div className="grid gap-4 sm:grid-cols-3">
          {PLUS_LOIN.map(({ to, label, sub, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:border-primary/40 hover:shadow-md"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" strokeWidth={2.1} aria-hidden />
              </span>
              <span className="flex-1">
                <span className="block text-sm font-semibold leading-snug text-primary">{label}</span>
                <span className="block text-xs text-muted-foreground">{sub}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
            </Link>
          ))}
        </div>
      </Section>

      <FinalCTA title="Demandez votre étude gratuite" />
    </div>
  );
}
